import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WEIGHT_UNIT_LABEL, TEMP_UNIT_LABEL, DATE_FORMAT_LABEL } from "@/lib/format";
import { toast } from "sonner";

const TIMEZONES = (typeof Intl !== "undefined" && Intl.supportedValuesOf)
  ? Intl.supportedValuesOf("timeZone")
  : ["UTC", "America/New_York", "Europe/London", "Europe/Vienna", "Asia/Tokyo"];

export default function PreferencesForm({ user, onSaved }) {
  const [form, setForm] = useState({
    weight_unit: user.weight_unit || "lb",
    temperature_unit: user.temperature_unit || "F",
    date_format: user.date_format || "mdy",
    timezone: user.timezone || "UTC",
    default_hive_id: user.default_hive_id || "none",
  });
  const [hives, setHives] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hives").then((r) => setHives(r.data)).catch(() => setHives([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", {
        ...form,
        default_hive_id: form.default_hive_id === "none" ? null : form.default_hive_id,
      });
      toast.success("Preferences saved");
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Weight unit">
          <Select value={form.weight_unit} onValueChange={(v) => setForm({ ...form, weight_unit: v })}>
            <SelectTrigger className="mt-1.5" data-testid="pref-weight-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(WEIGHT_UNIT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Temperature unit">
          <Select value={form.temperature_unit} onValueChange={(v) => setForm({ ...form, temperature_unit: v })}>
            <SelectTrigger className="mt-1.5" data-testid="pref-temp-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TEMP_UNIT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Date format">
          <Select value={form.date_format} onValueChange={(v) => setForm({ ...form, date_format: v })}>
            <SelectTrigger className="mt-1.5" data-testid="pref-date-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(DATE_FORMAT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Timezone">
          <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
            <SelectTrigger className="mt-1.5" data-testid="pref-timezone"><SelectValue placeholder={form.timezone || "UTC"} /></SelectTrigger>
            <SelectContent className="max-h-64">
              {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Default hive" hint="Pre-selected in Diagnose & new reminders.">
        <Select value={form.default_hive_id || "none"} onValueChange={(v) => setForm({ ...form, default_hive_id: v })}>
          <SelectTrigger className="mt-1.5" data-testid="pref-default-hive"><SelectValue placeholder="None" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {hives.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Button onClick={save} disabled={saving} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="pref-save-button">
        {saving ? "Saving…" : "Save preferences"}
      </Button>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">{label}</Label>
      {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
      {children}
    </div>
  );
}
